'use strict';
exports.default = exports;

const {TransactionType, BillingActions, TeamTypes} = require('../status');
const gql = require('graphql-tag').gql;
const BaseEntity = require('./BaseEntity.js').BaseEntity;
const moment = require('moment');


/**
 * Represents a financial transaction.
 * Standard DB fields managed by BaseEntity: id, active, created_at, updated_at, deleted_at.
 * GraphQL fragment exposes these as: id, active, createdAt, updatedAt, deletedAt.
 */
class TransactionEntity extends BaseEntity {
  action = null;
  id = null;
  active = true;
  procedureRowId = null;
  /** Optional reference to ProcedureTeamTab.rowId for per-procedure team assignment. Backward compatible: null for legacy transactions. */
  procedureTeamId = null;
  date = null;
  creationDate = null;
  value = 0;
  userId = '';
  userName = '';
  paid = false;
  transferId = null;
  appealId = null;
  cobrancaId = null;
  canceledById = null;
  canceledId = null;
  isAdditional = false;
  rowIdAdditionalData = null;
  eventId = null;
  companyId = null;
  matrizId = null;
  isMatriz = false;
  healthInsuranceId = null;
  batchesIds = [];
  receiverId = null; // old anesthetistId
  receiverType = TeamTypes.KANESTHETIST; //  status.TeamTypes    KANESTHETIST | KSECONDARY_ANESTHETIST | KSURGEON
  dtProcedure = null;
  hospitalId = null;
  type = null;
  financialItemId = null;

  /**
   *
   * @param {TransactionEntity|object?} obj
   */
  constructor(obj) {
    super(obj);
    this.setFcm(obj);
  }
  /**
   * Override opcional, serve para fazer operações adicionais.
   * Ao manipular as propriedades diretamente, as validações aqui feitas são ignoradas
   * @param {TransactionEntity|object?} obj
   */
  setFcm(obj) {
    if (obj?.anesthetistId) {
      console.error('anesthetistId foi migrado para receiverId, isso não deveria acontecer', this, obj);
    }
    if(typeof obj?.date?.toDate ==='function') {
      obj.date = obj.date.toDate();
    }
    if(typeof obj?.creationDate?.toDate ==='function') {
      obj.creationDate = obj.creationDate.toDate();
    }
    if(typeof obj?.dtProcedure?.toDate ==='function') {
      obj.dtProcedure = obj.dtProcedure.toDate();
    }
    if(obj?.date && typeof obj.date === 'string') {
      obj.date = moment(obj.date)?.toDate();
    }
    if(obj?.creationDate && typeof obj.creationDate === 'string') {
      obj.creationDate = moment(obj.creationDate)?.toDate();
    }
    if(obj?.dtProcedure && typeof obj.dtProcedure === 'string') {
      obj.dtProcedure = moment(obj.dtProcedure)?.toDate();
    }

    super.setFcm(obj);
    if(this.isAdditional) {
      this.type = TransactionType.COMPLEMENTATION;
    }
    if(!this.type) {
      this.type = TransactionType.PROCEDURE;
    }
    this.isAdditional = this.type === TransactionType.COMPLEMENTATION;
    return this;
  }

  example() {
    this.setFcm({
      action: BillingActions.Received,
      id: 'someId',
      active: true,
      procedureRowId: 'someIdOfRowId',
      procedureTeamId: null,
      date: new Date(),
      creationDate: new Date(),
      value: 123,
      userId: 'idFernanda',
      userName: 'Fernanda',
      paid: false,
      transferId: null,
      cobrancaId: null,
      canceledById: null,
      canceledId: null,
      isAdditional: false,
      rowIdAdditionalData: 'someAdditionalDataId',

      eventId: 'someEventId',
      companyId: 'someCompanyId',
      matrizId: null,
      isMatriz: false,
      healthInsuranceId: 'someHealthInsuranceId',
      batchesIds: [],
      dtProcedure: new Date(),
      hospitalId: 'someHospitalId',
      type: TransactionType.PROCEDURE,
      receiverType: TeamTypes.KANESTHETIST,
      receiverId: 'someAnesthetistIdOrMedicalId'
    });
    return this;
  }

  getFullMutation(key = 'id') {
    if (!this.batchesIds) {
      this.batchesIds = [];
    }

    const result = super.getFullMutation(key);

    // Strip fields that are intentionally NOT on the GraphQL Transaction type:
    // - `active` is not modeled on Transaction (cancellation uses `canceledId`).
    // - `procedureTeamId` persists in Firestore for the per-procedure-team feature
    //   (#710) but is not yet exposed by the Prisma-generated GraphQL schema.
    //   When the backend adds the column (`volanpg/prisma/schema.prisma`), this
    //   delete will be reverted and the field will flow through to Postgres.
    if (result?.create) {
      delete result.create.active;
      delete result.create.procedureTeamId;
    }
    if (result?.update) {
      delete result.update.active;
      delete result.update.procedureTeamId;
    }

    return result;
  }

  static toSheetRowHeader({transactionTypeName}) {
    const result = [];
    result.push('Guia');
    result.push('Paciente'); // 1
    result.push('Data');
    result.push('Convênio');
    result.push('Lote');
    result.push('Hospital');
    result.push('Procedimentos');
    result.push(`Total ${transactionTypeName}`);

    return result;
  }

  static toSheetRow({ numberProviderGuide, patient, formattedDtProcedureIso, healthInsuranceName, batchCode, hospitalName, proceduresArray, total }) {
    const result = [];

    result.push(numberProviderGuide);
    result.push(patient.name);
    result.push(formattedDtProcedureIso);
    result.push(healthInsuranceName);
    result.push(batchCode);
    result.push(hospitalName);
    result.push(proceduresArray.map(({name}) => name).join(', '));
    result.push(total);

    return result;
  }

  static toSheetWSCols() {
    return [25, 30, 35, 25, 25, 25, 35, 20];
  }
}

TransactionEntity._defaults = new TransactionEntity();


// NOTE: Two fields are intentionally NOT in this fragment because the server
// GraphQL Transaction type does not expose them:
//   - `active`: not modeled on Transaction (cancellation uses `canceledId`)
//   - `procedureTeamId`: persisted to Firestore by the per-procedure-team
//     feature (#710) but not yet exposed by the Prisma-generated GraphQL
//     schema. Tracked as a backend follow-up. Until then, GraphQL consumers
//     resolve a procedure's tab via the procedure document, not the
//     transaction document.
const TransactionFragment = gql`
  fragment TransactionFragment on Transaction {
    action
    receiverId
    receiverType
    batchesIds
    canceledById
    canceledId
    cobrancaId
    companyId
    creationDate
    date
    eventId
    healthInsuranceId
    hospitalId
    id
    isAdditional
    isMatriz
    matrizId
    paid
    procedureRowId
    transferId
    appealId
    rowIdAdditionalData
    userId
    userName
    type
    value
    createdAt
    createdBy
    createdByName
    updatedAt
    updatedBy
    updatedByName
    deletedAt
    deletedBy
    deletedByName
    dtProcedure
    financialItemId
  }
`;


exports.TransactionEntity = TransactionEntity;
exports.TransactionFragment = TransactionFragment;


/*

new TransactionEntity({
            action: this.extraData.inputAction,
            id: this.$newId(),
            procedureRowId: pIndex==='additional'?null:pIndex,
            date: Timestamp.now(),
            creationDate: Timestamp.now(),
            value: value,
            userId: this.currentUser?.id || '',
            userName: this.currentUser?.name || '',
            isAdditional: pIndex==='additional',
          })



          tr = {
          ...tr,
          eventId: this.eventId,
          companyId: form.companyId || null,
          matrizId: form.matrizId || null,
          isMatriz: form.isMatriz || false,
          healthInsuranceId: form.healthInsuranceId || null,
          batchesIds: form.batchesIds || null,
          anesthetistId: form.anesthetistId || null,
          hospitalId: form.hospitalId || null
        };

 */





